import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

// component
import AppRoutes from "./AppRoutes";
import Toaster from "./Components/UI/Toaster";

// redux
import { setLanguage } from "./Store/Actions/langaugeActionCreator";

// helper
import "./Language/i18n";

// Firebase FCM
import {
  initializeFCM,
  getFCMToken,
  getBrowserInstanceId,
  setupForegroundMessageHandling
} from "./firebase";
import { registerPushSubscription } from "./Network/Core/Notifications";

// css
import "./Assets/css/bootstrap.min.css";
import "./Assets/css/feather.min.css";
import "./Assets/css/style.css";
import "./Assets/css/custom.css";
import { getLocalStorage, setSession } from "Network/ApiService";
import { ls_vars } from "Library/Constants";
import { ThemeProvider } from "@mui/material";
import theme from "theme";
import toast from "react-hot-toast";

function App() {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const language = useSelector((state: any) => state?.language?.language);

  useEffect(() => {
    if (language !== undefined) {
      dispatch(setLanguage(language));
      i18n.changeLanguage(language);
    } else {
      dispatch(setLanguage("en"));
      i18n.changeLanguage("en");
    }
  }, [language]);

  const token = getLocalStorage(ls_vars.ACCESS_TOKEN);

  useEffect(() => {
    if (token) setSession(token);
  }, [token]);

  // Initialize Firebase Cloud Messaging
  useEffect(() => {
    const initFCM = async () => {
      // Check notification permission first
      
      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        
        if (permission === 'denied') {
          toast.error('Push notifications are disabled. Please enable them in browser settings.', {
            duration: 8000,
            position: 'top-right'
          });
          return;
        } else if (permission === 'granted') {
          toast.success('Push notifications enabled!', {
            duration: 3000,
            position: 'top-right'
          });
        }
      } else if (Notification.permission === 'denied') {
        toast.error('Push notifications are blocked. Please enable them in browser settings.', {
          duration: 8000,
          position: 'top-right'
        });
        return;
      }
      
      // Add a small delay to ensure DOM is fully ready
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Initialize FCM with message handler
      const result = await initializeFCM((payload) => {
        
        // Handle foreground messages with toast
        if (payload.notification) {
          toast.success(
            payload.notification.body || 'New notification received',
            {
              duration: 5000,
              position: 'top-right',
            }
          );
        }
      });

      if (result.success) {
        try {
          // Get FCM token for backend registration
          const fcmToken = await getFCMToken();
          if (fcmToken) {  
            const projectRef = getLocalStorage(ls_vars.PROJECT_REF);
            const browserInstanceId = getBrowserInstanceId();

            const response = await registerPushSubscription(projectRef, {
              fcm_token: fcmToken,
              platform: 'web',
              browser_instance_id: browserInstanceId,
              user_agent: navigator.userAgent
            });
            
            // Check for successful HTTP status codes (200 or 201)
            if (response?.status === 200 || response?.status === 201) {
              toast.success('Push notifications enabled successfully!', {
                duration: 3000,
                position: 'top-right'
              });
            } else {
              toast.error('Failed to enable push notifications', {
                duration: 5000,
                position: 'top-right'
              });
            }
          } else {
            toast.error('Could not get push notification token', {
              duration: 5000,
              position: 'top-right'
            });
          }
        } catch (error) {
          toast.error('Error setting up push notifications', {
            duration: 5000,
            position: 'top-right'
          });
        }
      } else {
        toast.error(`Push notifications unavailable: ${result.error}`, {
          duration: 5000,
          position: 'top-right'
        });
      }
    };

    // Only initialize FCM if user is logged in
    if (token) {
      // Use setTimeout to ensure this runs after the component is mounted
      setTimeout(() => {
        initFCM().catch((error) => {
          toast.error('Failed to initialize push notifications', {
            duration: 5000,
            position: 'top-right'
          });
        });
      }, 500);
    }
  }, [token]);

  return (
    <ThemeProvider theme={theme}>
      <AppRoutes />
      <Toaster />
    </ThemeProvider>
  );
}

export default App;
